/**
 * The traveller's start/end confirmation, surfaced on whatever page they are
 * already looking at.
 *
 * Both confirmations used to live only on the trip detail page, three steps in
 * (My Trips → the right tab → open the trip). A traveller standing next to
 * their assistant had no way to start the trip without going through their
 * history first, and until they did, the trip never started and the
 * assistant's fee stayed locked. This bar renders under the header on every
 * traveller page, and confirms in place — the link to the full trip is there
 * for anyone who wants the detail, not as the only way through.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Clock3, X } from "lucide-react";
import { useAuthStore } from "../hooks/useAuthStore";
import { useToast } from "../hooks/useToast";
import {
  useMyBookingsQuery,
  useConfirmTripStartMutation,
  useConfirmTripEndMutation,
} from "../api/query/useBookingQueries";
import { getTripsNeedingAction } from "../utils/myTrips";

const extractItems = (response) => {
  const items = response?.data?.items || response?.items || response?.data || [];
  return Array.isArray(items) ? items : [];
};

export default function TripActionBar() {
  const navigate = useNavigate();
  const toast = useToast();
  const { isAuthenticated, isUser } = useAuthStore();
  // Only the traveller has this handshake, and /bookings/mine is a user-only
  // endpoint — calling it as an assistant or admin would just 403.
  const enabled = isAuthenticated && isUser;

  const { data } = useMyBookingsQuery({ limit: 50 }, { enabled });
  const confirmStart = useConfirmTripStartMutation();
  const confirmEnd = useConfirmTripEndMutation();
  const [dismissed, setDismissed] = useState([]);

  if (!enabled) return null;

  const trips = getTripsNeedingAction(extractItems(data)).filter(
    (trip) => !dismissed.includes(trip.id)
  );
  if (trips.length === 0) return null;

  // One bar, one trip. Two confirmations pending at once is rare, and stacking
  // banners pushes the page the traveller actually opened off the screen.
  const trip = trips[0];
  const phase = trip.canConfirmEnd ? "end" : "start";
  const mutation = phase === "end" ? confirmEnd : confirmStart;

  const handleConfirm = async () => {
    try {
      const response = await mutation.mutateAsync({ id: trip.id });
      const updated = response?.data?.booking || response?.booking || null;
      // The server decides whether this was the second confirmation; trust the
      // booking it hands back rather than guessing the new status here.
      const bothDone =
        phase === "start"
          ? updated?.status === "trip_started"
          : updated?.status === "completed";
      toast.success(
        bothDone
          ? phase === "start"
            ? "Trip started."
            : "Trip completed."
          : "Confirmed. Waiting for your Pulo Assistant to confirm too."
      );
    } catch (err) {
      toast.apiError(err, "Unable to confirm right now. Please try again.");
    }
  };

  return (
    // Rendered inside the sticky header, so it travels with it rather than
    // guessing the header's height with an offset of its own.
    <div className="border-y border-[#F5D9A8] bg-[#FFF9EE]">
      <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3 sm:px-6">
        <Clock3 className="h-5 w-5 shrink-0 text-[#B26700]" />

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">
            {phase === "start"
              ? `Has your trip with ${trip.assistantName} started?`
              : `Has your trip with ${trip.assistantName} ended?`}
          </p>
          <button
            type="button"
            onClick={() => navigate(`/user/trips/${trip.id}`)}
            className="mt-0.5 inline-flex items-center gap-1 text-xs font-semibold text-[#61013B] underline decoration-2 underline-offset-2"
          >
            {trip.dateRange} · View trip
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={mutation.isPending}
            className="rounded-xl bg-[#61013B] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#4f0f32] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {mutation.isPending
              ? "Confirming..."
              : phase === "start"
              ? "Confirm trip started"
              : "Confirm trip ended"}
          </button>
          <button
            type="button"
            onClick={() => setDismissed((prev) => [...prev, trip.id])}
            aria-label="Hide this reminder"
            title="Hide until the next page load"
            className="rounded-lg p-2 text-[#B26700] transition hover:bg-[#F7ECD8]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
